import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { snackbar } from '@occc-app/design-system';
import {
  ConfirmDialog,
  Typography,
  TableRowActionsMenu,
  useConfirmDialog,
} from '@occc/ui-design-system';
import { Open_Commerce_User_Status } from '@occc/graphql';
import { useOpenCommerceUserUpdateMutation } from '@occc-app/dataAccessLayer/openCommerceUser/mutations';

import { routeCreator } from '../utils';

interface UsersActionsContainerProps {
  uuid: string;
  userPersonFullName: string | null;
  email: string;
  status: Open_Commerce_User_Status;
}

export const UsersActionsContainer: React.FC<UsersActionsContainerProps> = ({
  uuid,
  userPersonFullName,
  email,
  status,
}) => {
  const navigate = useNavigate();

  const onUserUpdateCompleted = () => snackbar.success('User has been successfully disabled.');

  const [userUpdateMutation] = useOpenCommerceUserUpdateMutation({
    onCompleted: onUserUpdateCompleted,
    refetchQueries: ['openCommerceUsers'],
  });

  const onUserDisableDialogConfirm = () =>
    userUpdateMutation({
      variables: {
        input: {
          uuid,
          status: Open_Commerce_User_Status.Disabled,
        },
      },
    });

  const confirmationAlertText = `Are you sure you would like to disable "${
    userPersonFullName || email
  }"? This will remove their access to Command Center but all audit logs will be preserved.`;

  const { open, setOpen, onCloseButtonClick, onConfirmButtonClick } = useConfirmDialog({
    onConfirm: onUserDisableDialogConfirm,
    shouldStopEventPropagation: true,
  });

  const menuItems = useMemo(() => {
    const items = [];

    items.push({
      label: 'Edit User',
      onClick: () => navigate(routeCreator.update(uuid)),
      dataTestId: 'edit',
    });

    if (status !== Open_Commerce_User_Status.Disabled) {
      items.push({
        label: 'Disable User',
        onClick: () => setOpen(true),
        dataTestId: 'Disable',
      });
    }

    return items;
  }, [status, navigate, uuid, setOpen]);

  return (
    <>
      <TableRowActionsMenu menuItems={menuItems} />
      <ConfirmDialog
        dialogTitle='Disable User'
        open={open}
        onClose={onCloseButtonClick}
        confirmButtonText='Confirm'
        confirmButtonProps={{
          onClick: onConfirmButtonClick,
          color: 'error',
        }}
      >
        <Typography>{confirmationAlertText}</Typography>
      </ConfirmDialog>
    </>
  );
};
